/**
 * Renders a foreign key as the name it points at.
 *
 * The SAFE mapping tables are almost entirely foreign keys — `risk_to_capability` is a risk id
 * and a capability id and nothing else — so a raw table of them is unreadable. The serializers
 * return ids (`fields = '__all__'` with the default `PrimaryKeyRelatedField`) and the generic
 * model API has no `depth` on these routes, so the resolution has to happen here.
 *
 * It is a component rather than a plain function because it needs a hook. That is deliberate and
 * cheap: every cell in a column shares one react-query key, so a 400-row join table issues *one*
 * request for the reference list, not 400. The hour-long `staleTime` matches the SAFE reference
 * hooks — capability and pin names change when someone edits them in the tab next door, and the
 * `CacheKey.Admin` invalidation on save takes care of that case.
 *
 * A missing id renders as `#<id>` rather than blank, so a dangling mapping row is visible instead
 * of looking like an empty column.
 */
import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query'; //-tech-debt-react-query;
import { fetchItems } from '../../../support/queries';
import { CacheKey } from '../../../types/cache-keys';

const REFERENCE_STALE_TIME = 60 * 60 * 1000;

type RefRow = Record<string, unknown>;

/** id -> label for one reference endpoint. */
const useRefLookup = (
  endpoint: string,
  labelField: string,
  params?: Record<string, unknown>,
): Record<string, string> => {
  const query = useQuery({
    queryKey: [CacheKey.Admin, 'safeRef', endpoint, params || {}],
    queryFn: () => fetchItems(endpoint, params || {}),
    staleTime: REFERENCE_STALE_TIME,
  });

  return useMemo(() => {
    const rows: RefRow[] = Array.isArray(query.data) ? (query.data as RefRow[]) : [];
    const lookup: Record<string, string> = {};

    rows.forEach((row) => {
      if (row?.id == null) return;

      const label = row[labelField] ?? row.name ?? row.title ?? row.id;
      lookup[String(row.id)] = String(label);
    });

    return lookup;
  }, [query.data, labelField]);
};

type SafeRefNameProps = {
  endpoint: string;
  value: unknown;
  labelField?: string;
  params?: Record<string, unknown>;
};

export const SafeRefName: React.FC<SafeRefNameProps> = ({
  endpoint,
  value,
  labelField = 'name',
  params,
}) => {
  const lookup = useRefLookup(endpoint, labelField, params);

  // A nested object arrives when a route is queried with `detailed`; none of the SAFE routes are,
  // but handling it keeps the renderer usable if one gains the parameter later.
  if (value && typeof value === 'object') {
    const row = value as RefRow;
    return <>{String(row[labelField] ?? row.name ?? row.id ?? '')}</>;
  }

  if (value == null || value === '') return <>&mdash;</>;

  return <>{lookup[String(value)] ?? `#${value}`}</>;
};

/**
 * Column `render` for a foreign key field.
 *
 * Lives here rather than in `RbacConfig.ts` because that file is `.ts` and cannot hold JSX.
 */
export const safeRefRender = (
  endpoint: string,
  key: string,
  labelField = 'name',
  params?: Record<string, unknown>,
) => {
  const render = (row: any): React.ReactNode => (
    <SafeRefName endpoint={endpoint} value={row?.[key]} labelField={labelField} params={params} />
  );

  // Named so the lint rule that treats any function returning JSX as a component is satisfied,
  // and so a column shows up as something legible in the React devtools tree.
  render.displayName = `SafeRefName(${endpoint}.${key})`;

  return render;
};

export default SafeRefName;
